import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';

// PrimeNG imports
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { Menu } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { PanelModule } from 'primeng/panel';
import { DividerModule } from 'primeng/divider';
import { CheckboxModule } from 'primeng/checkbox';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { RingoverClickToCallComponent } from '../../../cac/components/shared/ringover-click-to-call/ringover-click-to-call.component';

import { ComparisonHistoryService } from '../../services/comparison-history.service';
import { ComparisonHistory } from '../../models/comparison-history.model';
import { RingoverService } from '../../../../shared/services/ringover.service';
import { AuthService } from '../../services/auth.service';
import { ProjectHistoryService } from '../../services/project-history.service';

@Component({
  selector: 'app-comparison-history',
  templateUrl: './comparison-history.component.html',
  styleUrls: ['./comparison-history.component.scss'],
  standalone: true,
  imports: [
    CommonModule, FormsModule, TableModule, ButtonModule, CardModule, TagModule,
    TooltipModule, DialogModule, ConfirmDialogModule, ToastModule, InputTextModule,
    DropdownModule, CalendarModule, PanelModule, DividerModule, CheckboxModule, MenuModule,
    RingoverClickToCallComponent
  ],
  providers: [ConfirmationService, MessageService]
})
export class ComparisonHistoryComponent implements OnInit, OnDestroy {
  @ViewChild('smsMenu') smsMenu!: Menu;
  @ViewChild('csvImportInput') csvImportInput?: any;
  history: ComparisonHistory[] = [];
  filteredHistory: ComparisonHistory[] = [];
  selectedComparison: ComparisonHistory | null = null;
  showDetailDialog = false;
  smsRecipientPhone: string = '';
  
  // Sélection multiple
  selectedComparisons: string[] = [];
  selectAllChecked = false;
  
  // Filtres
  searchText = '';
  selectedUser = '';
  dateRange: Date[] = [];

  // Nouveaux filtres (visuels) pour correspondre au CAC
  typeProjetFilter = [
    { label: 'Tous', value: '' },
    { label: 'Particulier', value: 'particulier' },
    { label: 'Professionnel', value: 'professionnel' }
  ];
  origineFilter = [{ label: 'Site Web', value: 'web' }, { label: 'Import', value: 'import' }];
  statusFilter = [
    { label: 'À traiter', value: 'a_traiter' },
    { label: 'En cours', value: 'en_cours' },
    { label: 'Signé', value: 'signe' },
    { label: 'Perdu', value: 'perdu' }
  ];
  emailValidityFilter = [
    { label: 'Valide', value: 'valid' },
    { label: 'Invalide', value: 'invalid' },
    { label: 'Non testé', value: 'untested' }
  ];
  niveauFilter = [
    { label: 'Niveau 1 (> 100€)', value: 'n1' },
    { label: 'Niveau 2 (40€ - 80€)', value: 'n2' },
    { label: 'Niveau 3 (< 40€)', value: 'n3' }
  ];

  selectedTypeProjet: string = '';
  selectedOrigine: string = '';
  selectedStatus: string = '';
  selectedEmailValidity: string = '';
  selectedNiveau: string = '';

  
  // Options pour les filtres
  userOptions: { label: string, value: string }[] = [];
  
  // Statistiques
  statistics: any = {};
  userStatusCounts: any[] = [];
  
  // SMS
  showSmsDialog = false;
  smsType: 'rdv' | 'devis_envoye' | 'relance' | '' = '';
  smsContent = '';
  rdvTime = '14H30';
  sendingSms = false;
  smsMenuItems: MenuItem[] = [];
  
  // Subscriptions
  private historySubscription?: Subscription;
  
  // Colonnes du tableau
  cols = [
    { field: 'date', header: 'Date' },
    { field: 'userName', header: 'Utilisateur' },
    { field: 'principalName', header: 'Assuré Principal' },
    { field: 'conjoint', header: 'Conjoint' },
    { field: 'enfants', header: 'Enfants' },
    { field: 'totalResults', header: 'Résultats' },
    { field: 'actions', header: 'Actions' }
  ];

  // Email bulk actions
  sendingSavingsEmail = false;
  resendingDevis = false;
  sendingSavingsEmailId: string | number | null = null;
  resendingDevisId: string | number | null = null;

  importingCsv: boolean = false;
  deletingByMobileExcel: boolean = false;

  // Actions globales
  selectedAction: string = '';

  get actionMenuOptions(): { label: string; value: string }[] {
    const options: { label: string; value: string }[] = [];
    options.push({ label: 'Importer des comparaisons (CSV/Excel)', value: 'import_csv' });
    options.push({ label: 'Exporter en CSV', value: 'export_csv' });
    options.push({ label: 'Vider l\'historique', value: 'clear_history' });

    options.push({ label: 'Changer le statut de la sélection', value: 'change_status' });
    options.push({ label: 'Attribuer la sélection', value: 'assign' });
    options.push({ label: 'Envoyer une demande d\'information', value: 'send_info_email' });
    options.push({ label: 'Envoyer un email d\'économies', value: 'send_savings_email' });
    options.push({ label: 'Renvoyer SMS + Devis', value: 'send_sms_devis' });
    options.push({ label: 'Envoyer un email Perdu', value: 'send_perdu_email' });
    options.push({ label: 'Supprimer la sélection', value: 'delete_selection' });

    return options;
  }

  onActionMenuChange(): void {
    // Reset secondary states if any
  }

  canExecuteSelectedAction(): boolean {
    if (!this.selectedAction) return false;
    
    // Actions nécessitant une sélection
    const actionsRequiringSelection = [
      'delete_selection', 
      'change_status', 
      'assign', 
      'send_info_email', 
      'send_savings_email', 
      'send_sms_devis',
      'send_perdu_email'
    ];
    
    if (actionsRequiringSelection.includes(this.selectedAction)) {
      return this.selectedComparisons.length > 0 || this.selectAllChecked;
    }
    
    return true; // import, export, clear all can be done anytime
  }

  isExecutingSelectedAction(): boolean {
    if (this.selectedAction === 'import_csv') return this.importingCsv;
    return false;
  }

  getCommentaire(comparison: any): string {
    if (comparison.commentaire) return comparison.commentaire;
    const name = `${comparison.principalCivility ? comparison.principalCivility + ' ' : ''}${comparison.principalName || ''} ${comparison.principalFirstName || ''}`.trim();
    const key = `cas_notes_${name.replace(/[^a-zA-Z0-9]/g, '_')}`;
    return localStorage.getItem(key) || '';
  }

  executeSelectedAction(): void {
    if (!this.selectedAction) return;
    
    switch (this.selectedAction) {
      case 'import_csv':
        this.triggerCsvImport();
        break;
      case 'export_csv':
        this.exportToCsv();
        break;
      case 'clear_history':
        this.clearAllHistory();
        break;
      case 'delete_selection':
        this.deleteSelectedComparisons();
        break;
      case 'change_status':
      case 'assign':
        this.messageService.add({
          severity: 'info',
          summary: 'Information',
          detail: 'Cette action CRM (statut/attribution) est native au CAC et sera bientôt étendue au CAS.'
        });
        break;
      case 'send_info_email':
        this.messageService.add({ severity: 'info', summary: 'Envoi en cours', detail: "Préparation des emails de demande d'information..." });
        this.projectHistoryService.bulkSendInfoEmail(this.selectedComparisons).subscribe({
          next: (res) => {
            this.messageService.add({ severity: 'success', summary: 'Succès', detail: res.message || 'Emails envoyés' });
            this.clearSelection();
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Erreur', detail: "Erreur lors de l'envoi." });
          }
        });
        break;
      case 'send_savings_email':
        this.messageService.add({ severity: 'info', summary: 'Envoi en cours', detail: "Préparation des emails d'économies..." });
        this.projectHistoryService.bulkStepOneSavingsEmail(this.selectedComparisons).subscribe({
          next: (res) => {
            this.messageService.add({ severity: 'success', summary: 'Succès', detail: res.message || 'Emails envoyés' });
            this.clearSelection();
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Erreur', detail: "Erreur lors de l'envoi." });
          }
        });
        break;
      case 'send_perdu_email':
        this.messageService.add({ severity: 'info', summary: 'Envoi en cours', detail: 'Préparation des emails Perdu...' });
        this.projectHistoryService.bulkSendPerduEmail(this.selectedComparisons).subscribe({
          next: (res) => {
            this.messageService.add({ severity: 'success', summary: 'Succès', detail: res.message || 'Emails envoyés' });
            this.clearSelection();
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Erreur', detail: "Erreur lors de l'envoi." });
          }
        });
        break;
      case 'send_sms_devis':
        this.messageService.add({ severity: 'info', summary: 'Envoi en cours', detail: 'Préparation du renvoi de devis/SMS...' });
        this.projectHistoryService.bulkResendDevisEmail(this.selectedComparisons).subscribe({
          next: (res) => {
            this.messageService.add({ severity: 'success', summary: 'Succès', detail: res.message || 'Devis renvoyés' });
            this.clearSelection();
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Erreur', detail: "Erreur lors de l'envoi." });
          }
        });
        break;
    }
    // Reset after execution for single-click actions
    if (this.selectedAction !== 'import_csv') {
      this.selectedAction = '';
    }
  }

  triggerCsvImport(): void {
    if (this.csvImportInput) {
      this.csvImportInput.nativeElement.click();
    }
  }

  hasActiveFilters(): boolean {
    return !!(this.searchText || this.selectedUser || (this.dateRange && this.dateRange.length > 0 && this.dateRange[0]));
  }

  constructor(
    private comparisonHistoryService: ComparisonHistoryService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private ringoverService: RingoverService,
    private authService: AuthService,
    private projectHistoryService: ProjectHistoryService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadHistory();
    this.loadStatistics();
    // Rafraîchir depuis la base de données
    this.refreshFromDatabase();
  }

  ngOnDestroy(): void {
    if (this.historySubscription) {
      this.historySubscription.unsubscribe();
    }
  }

  /**
   * Charger l'historique des comparaisons
   */
  private loadHistory(): void {
    this.historySubscription = this.comparisonHistoryService.getHistory().subscribe(
      history => {
        this.history = history;
        this.filteredHistory = [...history];
        this.updateUserOptions();
        this.buildUserStatusCounts(history);
        this.applyFilters();
      }
    );
  }

  private buildUserStatusCounts(history: ComparisonHistory[]): void {
    const countsMap = new Map<string, any>();
    
    for (const item of history) {
      const email = item.userEmail || 'inconnu';
      if (!countsMap.has(email)) {
        countsMap.set(email, {
          user_email: email,
          user_name: item.userName || email,
          total_projects: 0,
          a_traiter_count: 0,
          traite_count: 0
        });
      }
      
      const counts = countsMap.get(email)!;
      counts.total_projects++;
      if (item.totalResults > 0) {
        counts.traite_count++;
      } else {
        counts.a_traiter_count++;
      }
    }
    
    this.userStatusCounts = Array.from(countsMap.values()).sort((a, b) => b.total_projects - a.total_projects);
  }

  /**
   * Charger les statistiques
   */
  private loadStatistics(): void {
    this.statistics = this.comparisonHistoryService.getStatistics();
  }

  /**
   * Rafraîchir l'historique depuis la base de données
   */
  refreshFromDatabase(): void {
    this.comparisonHistoryService.loadHistoryFromDatabase();
    this.messageService.add({
      severity: 'info',
      summary: 'Actualisation',
      detail: 'Chargement de l\'historique depuis la base de données...'
    });
  }

  /**
   * Mettre à jour les options utilisateur pour le filtre
   */
  private updateUserOptions(): void {
    const uniqueUsers = [...new Set(this.history.map(h => h.userEmail))];
    this.userOptions = [
      { label: 'Tous les utilisateurs', value: '' },
      ...uniqueUsers.map(email => ({ label: email, value: email }))
    ];
  }

  /**
   * Appliquer les filtres
   */
  applyFilters(): void {
    let filtered = [...this.history];

    // Filtre par texte de recherche
    if (this.searchText.trim()) {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(item =>
        item.userName.toLowerCase().includes(searchLower) ||
        item.principalName.toLowerCase().includes(searchLower) ||
        item.principalFirstName.toLowerCase().includes(searchLower) ||
        (item.userEmail || '').toLowerCase().includes(searchLower) ||
        (item.conjoint?.name || '').toLowerCase().includes(searchLower) ||
        (item.conjoint?.firstName || '').toLowerCase().includes(searchLower)
      );
    }

    // Filtre par utilisateur
    if (this.selectedUser) {
      filtered = filtered.filter(item => item.userEmail === this.selectedUser);
    }

    // Filtre par statut
    if (this.selectedStatus) {
      if (this.selectedStatus === 'a_traiter') {
        filtered = filtered.filter(item => item.totalResults === 0);
      } else if (this.selectedStatus === 'traite' || this.selectedStatus === 'en_cours' || this.selectedStatus === 'signe') {
        // Dans CAS, "traité" correspond à des résultats existants.
        filtered = filtered.filter(item => item.totalResults > 0);
      } else if (this.selectedStatus === 'perdu') {
        filtered = []; // Not tracked natively in CAS history
      }
    }

    // Filtre par type de projet
    if (this.selectedTypeProjet) {
      filtered = filtered.filter(item => {
        const regime = (item.regimeObligatoire || '').toLowerCase();
        const isPro = regime.includes('tns') || regime.includes('indépendant') || regime.includes('ssi');
        if (this.selectedTypeProjet === 'professionnel') return isPro;
        if (this.selectedTypeProjet === 'particulier') return !isPro;
        return true;
      });
    }

    // Filtre par Niveau (Cotisation)
    if (this.selectedNiveau) {
      filtered = filtered.filter(item => {
        if (!item.results || item.results.length === 0) return false;
        // Trouver le prix minimum parmi les résultats
        const minPrix = Math.min(...item.results.map((r: any) => parseFloat(r.prix) || 0).filter((p: number) => p > 0));
        if (minPrix === Infinity || minPrix === 0) return false;
        
        if (this.selectedNiveau === 'n1') return minPrix >= 100;
        if (this.selectedNiveau === 'n2') return minPrix >= 40 && minPrix < 80;
        if (this.selectedNiveau === 'n3') return minPrix < 40;
        return false; // Exclut les prix entre 80 et 100 si un niveau spécifique est sélectionné (comme demandé par les règles strictes)
      });
    }

    // Filtre par Origine
    if (this.selectedOrigine) {
      filtered = filtered.filter(item => item.origine === this.selectedOrigine);
    }

    // Filtre par Validité Email
    if (this.selectedEmailValidity) {
      filtered = filtered.filter(item => item.emailValidity === this.selectedEmailValidity);
    }

    // Filtre par plage de dates
    if (this.dateRange && this.dateRange.length === 2 && this.dateRange[0] && this.dateRange[1]) {
      const startDate = this.dateRange[0];
      const endDate = this.dateRange[1];
      // Include end of day for endDate
      const endDateMax = new Date(endDate);
      endDateMax.setHours(23, 59, 59, 999);
      
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date);
        return itemDate >= startDate && itemDate <= endDateMax;
      });
    }

    this.filteredHistory = filtered;
  }

  /**
   * Réinitialiser les filtres
   */
  resetFilters(): void {
    this.searchText = '';
    this.selectedUser = '';
    this.dateRange = [];
    this.selectedTypeProjet = '';
    this.selectedOrigine = '';
    this.selectedStatus = '';
    this.selectedEmailValidity = '';
    this.selectedNiveau = '';
    this.applyFilters();
  }

  /**
   * Afficher les détails d'une comparaison
   */
  projectPhoneE164(comparison: ComparisonHistory): string | null {
    // Si pas de téléphone, on met un numéro factice juste pour afficher Ringover pour la démo UI
    const raw = (comparison.principalPhone || '0600000000').trim();
    const digits = raw.replace(/\D/g, '');
    if (!digits || digits.length < 8) return '+33600000000';
    if (digits.startsWith('00')) return `+${digits.slice(2)}`;
    if (digits.startsWith('33')) return `+${digits}`;
    if (digits.startsWith('0')) return `+33${digits.slice(1)}`;
    return `+${digits}`;
  }

  loadComparison(comparison: ComparisonHistory): void {
    this.comparisonHistoryService.setComparisonToLoad(comparison);
    this.router.navigate(['/cas/compare']);
  }

  duplicateComparison(comparison: ComparisonHistory): void {
    this.messageService.add({severity:'info', summary:'Information', detail:'Fonctionnalité en cours de développement.'});
  }

  openTransaction(comparison: ComparisonHistory): void {
    this.messageService.add({severity:'info', summary:'Information', detail:'Fonctionnalité en cours de développement.'});
  }

  showDetails(comparison: ComparisonHistory): void {
    this.selectedComparison = comparison;
    this.showDetailDialog = true;
  }

  /**
   * Supprimer une comparaison
   */
  deleteComparison(comparison: ComparisonHistory): void {
    this.confirmationService.confirm({
      message: `Êtes-vous sûr de vouloir supprimer cette comparaison du ${this.formatDate(comparison.date)} ?`,
      header: 'Confirmation de suppression',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.comparisonHistoryService.deleteComparison(comparison.id);
        this.messageService.add({
          severity: 'success',
          summary: 'Suppression réussie',
          detail: 'La comparaison a été supprimée avec succès.'
        });
        this.selectedComparisons = this.selectedComparisons.filter(id => id !== comparison.id);
        this.loadStatistics();
      }
    });
  }

  /**
   * Sélectionner/Désélectionner une comparaison
   */
  toggleSelection(comparison: ComparisonHistory): void {
    const index = this.selectedComparisons.indexOf(comparison.id);
    if (index > -1) {
      this.selectedComparisons.splice(index, 1);
    } else {
      this.selectedComparisons.push(comparison.id);
    }
    this.updateSelectAllState();
  }

  /**
   * Vérifier si une comparaison est sélectionnée
   */
  isSelected(id: string): boolean {
    return this.selectedComparisons.includes(id);
  }

  /**
   * Sélectionner toutes les comparaisons
   */
  selectAll(): void {
    this.selectedComparisons = this.filteredHistory.map(item => item.id);
    this.filteredHistory.forEach(item => item.selected = true);
    this.selectAllChecked = true;
  }

  /**
   * Désélectionner toutes les comparaisons
   */
  clearSelection(): void {
    this.selectedComparisons = [];
    this.filteredHistory.forEach(item => item.selected = false);
    this.selectAllChecked = false;
  }

  /**
   * Toggle sélection de toutes les comparaisons
   */
  toggleSelectAll(event: any): void {
    if (event.checked) {
      this.selectAll();
    } else {
      this.clearSelection();
    }
  }

  /**
   * Mettre à jour l'état de la case "tout sélectionner"
   */
  private updateSelectAllState(): void {
    this.selectAllChecked = this.filteredHistory.length > 0 && 
                           this.selectedComparisons.length === this.filteredHistory.length;
  }

  /**
   * Supprimer les comparaisons sélectionnées
   */
  deleteSelectedComparisons(): void {
    if (this.selectedComparisons.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aucune sélection',
        detail: 'Veuillez sélectionner au moins une comparaison à supprimer.'
      });
      return;
    }

    this.confirmationService.confirm({
      message: `Êtes-vous sûr de vouloir supprimer ${this.selectedComparisons.length} comparaison(s) ? Cette action est irréversible.`,
      header: 'Confirmation de suppression multiple',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.comparisonHistoryService.deleteMultipleComparisons(this.selectedComparisons);
        this.messageService.add({
          severity: 'success',
          summary: 'Suppression réussie',
          detail: `${this.selectedComparisons.length} comparaison(s) supprimée(s) avec succès.`
        });
        this.selectedComparisons = [];
        this.selectAllChecked = false;
        this.loadStatistics();
      }
    });
  }

  /**
   * Vider tout l'historique
   */
  clearAllHistory(): void {
    this.confirmationService.confirm({
      message: 'Êtes-vous sûr de vouloir supprimer tout l\'historique ? Cette action est irréversible.',
      header: 'Confirmation de suppression totale',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.comparisonHistoryService.clearHistory();
        this.messageService.add({
          severity: 'success',
          summary: 'Historique vidé',
          detail: 'Tout l\'historique a été supprimé avec succès.'
        });
        this.loadStatistics();
      }
    });
  }

  /**
   * Exporter l'historique en CSV
   */
  exportToCsv(): void {
    const csvData = this.filteredHistory.map(item => {
      // Calcul du niveau
      let niveau = 'Sans cotation'; // Valeur par défaut s'il n'y a pas de résultats
      if (item.results && item.results.length > 0) {
        const minPrix = Math.min(...item.results.map((r: any) => parseFloat(r.prix) || 0).filter((p: number) => p > 0));
        if (minPrix !== Infinity && minPrix > 0) {
          if (minPrix < 40) niveau = 'Niveau 3';
          else if (minPrix >= 40 && minPrix < 80) niveau = 'Niveau 2';
          else if (minPrix >= 100) niveau = 'Niveau 1';
          else niveau = 'Non classé (80-100)'; // Si le montant est entre 80 et 100
        }
      }

      // Calcul du projet
      const regime = (item.regimeObligatoire || '').toLowerCase();
      const isPro = regime.includes('tns') || regime.includes('indépendant') || regime.includes('ssi');
      const typeProjet = isPro ? 'Professionnel' : 'Particulier';

      return {
        'Date': this.formatDate(item.date),
        'Type': typeProjet,
        'Niveau': niveau,
        'Utilisateur': item.userName,
        'Email': item.userEmail,
        'Assuré Principal': `${item.principalFirstName} ${item.principalName}`,
        'Date Naissance Principal': item.principalBirthDate,
        'Âge Principal': item.principalAge,
        'Conjoint': item.conjoint ? `${item.conjoint.firstName} ${item.conjoint.name}` : 'Non',
        'Nombre Enfants': item.enfants?.count || 0,
        'Régime Obligatoire': item.regimeObligatoire,
        'Code Postal': item.codePostal,
        'Date Effet': item.dateEffet,
        'Situation Familiale': item.situationFamiliale,
        'Nombre Résultats': item.totalResults
      };
    });

    this.downloadCsv(csvData, 'historique_comparaisons.csv');
  }

  /**
   * Télécharger un fichier CSV
   */
  private downloadCsv(data: any[], filename: string): void {
    if (data.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aucune donnée',
        detail: 'Aucune donnée à exporter.'
      });
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(';'),
      ...data.map(row => headers.map(header => {
        const val = row[header];
        const strVal = (val !== undefined && val !== null) ? val.toString() : '';
        return `"${strVal.replace(/"/g, '""')}"`;
      }).join(';'))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Formater une date pour l'export Excel (évite les problèmes de locale)
   */
  formatDate(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} à ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  /**
   * Retourne les initiales du client
   */
  getClientInitials(comparison: ComparisonHistory): string {
    const p = comparison.principalFirstName?.charAt(0) ?? '';
    const n = comparison.principalName?.charAt(0) ?? '';
    if (p || n) {
      return (p + n).toUpperCase();
    }
    if (comparison.userEmail) {
      return comparison.userEmail.substring(0, 2).toUpperCase();
    }
    return '?';
  }

  /**
   * Obtenir le texte de la composition familiale
   */
  getFamilyComposition(comparison: ComparisonHistory): string {
    if (!comparison.conjoint && !comparison.enfants) {
      return 'Célibataire';
    }
    
    const parts = [];
    if (comparison.conjoint) {
      parts.push('En couple');
    } else {
      parts.push('Famille monoparentale');
    }
    
    if (comparison.enfants && comparison.enfants.count > 0) {
      parts.push(`+ ${comparison.enfants.count} enfant(s)`);
    }
    
    return parts.join(' ');
  }

  // --- SMS LOGIC ---

  openSmsMenu(event: Event, comparison: ComparisonHistory): void {
    this.selectedComparison = comparison;
    this.smsMenuItems = [
      {
        label: 'SMS RDV manqué / NRP',
        icon: 'pi pi-mobile',
        command: () => this.prepareSms('rdv')
      },
      {
        label: 'SMS Devis Envoyé',
        icon: 'pi pi-mobile',
        command: () => this.prepareSms('devis_envoye')
      },
      {
        label: 'Renvoi DEVIS+SMS',
        icon: 'pi pi-mobile',
        command: () => this.prepareSms('relance')
      }
    ];
    
    // Use setTimeout to ensure the menu is updated before toggling
    setTimeout(() => {
      if (this.smsMenu) {
        this.smsMenu.toggle(event);
      }
    });
  }

  prepareSms(type: 'rdv' | 'devis_envoye' | 'relance'): void {
    if (!this.selectedComparison) return;
    this.smsType = type;
    this.rdvTime = '14H30';
    this.smsRecipientPhone = this.selectedComparison.principalPhone || '';
    this.updateSmsContent();
    this.showSmsDialog = true;
  }

  updateSmsContent(): void {
    if (!this.selectedComparison) return;
    
    const nom = this.selectedComparison.principalName || '';
    const prenom = this.selectedComparison.principalFirstName || '';
    const civ = this.selectedComparison.principalCivility || '';
    
    let greeting = '';
    if (civ.toLowerCase().includes('mme') || civ.toLowerCase().includes('madame') || civ.toLowerCase() === 'f') {
      greeting = `Chère Mme. ${prenom}`;
    } else if (civ.toLowerCase().includes('m.') || civ.toLowerCase().includes('monsieur') || civ.toLowerCase() === 'm') {
      greeting = `Cher M. ${nom}`;
    } else {
      greeting = `Cher(e) ${nom}`.trim();
    }
    
    const user = this.authService.getCurrentUser();
    const signatureName = user ? (user.prenom + ' ' + user.nom) : 'Votre conseiller';
    const signaturePhone = user?.telephone || '0143271243'; // Default to fixed line if no phone
    const signatureEmail = user?.email || 'contact@brokins.fr';

    // Dynamic Date
    const today = new Date();
    const formattedDate = today.toLocaleDateString('fr-FR');
    
    let content = '';

    if (this.smsType === 'rdv') {
      content = `${greeting},

Nous avions RDV téléphonique le ${formattedDate} à ${this.rdvTime} pour des économies sur votre assurance mutuelle Santé.
Nous sommes le 1er courtier en Assurance Mutuelle Santé et Crédit. Nous pouvons atteindre jusqu'à 30% d'économies dès le mois de [M+1] grâce à des accords particuliers avec nos assureurs. 
Vous n'avez rien faire, juste 4 minutes de votre temps suffisent.
Prestation sans frais.

Renseignez-vous sur le sérieux de notre Société de Courtage.

Bien à vous

${signatureName}
${signaturePhone}
${signatureEmail}
Cabinet BROKINS
www.brokins.fr`;
    } else if (this.smsType === 'devis_envoye') {
      // Dynamic Devies & Insurers
      let nbDevis = 4;
      let assureursList: string[] = [];
      
      const formatInsurerName = (r: any): string => {
        let name = r.assureur || r.insurer || r.assurance || 'Partenaire';
        if (typeof name === 'string') {
          if (name.includes(' - ')) {
            name = name.split(' - ')[0];
          }
          name = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
        }
        return name.toString().trim();
      };
      
      if (this.selectedComparison.results && this.selectedComparison.results.length > 0) {
         // Filter by selectedForQuote OR selectedForEmail (since we might have old records)
         let checkedProducts = this.selectedComparison.results.filter(r => r.selectedForQuote || (r as any).selectedForEmail);
         
         // If none were selected (legacy history record), just take the first 4
         if (checkedProducts.length === 0) {
           nbDevis = Math.min(4, this.selectedComparison.results.length);
           assureursList = this.selectedComparison.results.slice(0, nbDevis).map(formatInsurerName);
         } else {
           nbDevis = checkedProducts.length;
           assureursList = checkedProducts.map(formatInsurerName);
         }
      }

      const assureursUnique = Array.from(new Set(assureursList));
      let assureurs = assureursUnique.length > 0 ? assureursUnique.join(', ') : "nos partenaires";

      content = `${greeting},

Je me permets de revenir vers vous au sujet de votre demande de devis assurance mutuelle Santé.
Je vous ai envoyé nos ${nbDevis} meilleurs devis : ${assureurs}.
N'hésitez pas à revenir vers nous, nous sommes ouverts tous les jours jusqu'à 19h30.

Bien à vous

${signatureName}
${signaturePhone}
${signatureEmail}
Cabinet BROKINS
www.brokins.fr`;
    } else if (this.smsType === 'relance') {
      content = `${greeting},

Vous nous avez sollicités pour des devis en assurance Mutuelle Santé. Nous vous avions envoyé plusieurs devis.
Votre besoin est-il toujours d'actualité ? 
Le cas échéant, pouvons-nous échanger 5 minutes ? 

Bien à vous

${signatureName}
${signaturePhone}
${signatureEmail}
Cabinet BROKINS
www.brokins.fr`;
    }

    this.smsContent = content;
  }

  sendSms(): void {
    if (!this.smsContent.trim() || !this.selectedComparison) return;
    
    // Si c'est un Renvoi DEVIS+SMS, on envoie d'abord le devis par email
    if (this.smsType === 'relance') {
      const projectId = this.selectedComparison.id;
      if (projectId) {
        this.projectHistoryService.bulkResendDevisEmail([projectId]).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Devis envoyé', detail: 'Devis renvoyé par email.' });
          },
          error: (err: any) => {
            console.error('Erreur renvoi devis email:', err);
            this.messageService.add({ severity: 'warn', summary: 'Attention', detail: 'Échec du renvoi du devis par email.' });
          }
        });
      }
    }

    // Récupérer le numéro de téléphone
    let phone = this.selectedComparison.principalPhone || '';
    
    if (!phone) {
      if (this.smsType === 'relance') {
        // Renvoi DEVIS+SMS : le devis part quand même, on ferme la fenêtre
        this.messageService.add({ severity: 'success', summary: 'Devis envoyé', detail: 'Devis envoyé par email. Aucun SMS envoyé (numéro manquant).' });
        this.showSmsDialog = false;
        return;
      }
      const promptedPhone = prompt("Numéro de téléphone du client introuvable dans l'historique. Veuillez le saisir pour envoyer le SMS (ex: 06... ou +336...) :");
      if (!promptedPhone) {
        return;
      }
      phone = promptedPhone;
    }

    this.sendingSms = true;
    this.ringoverService.sendSms({ toNumber: phone, message: this.smsContent }).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Succès', detail: this.smsType === 'relance' ? 'Devis et SMS envoyés avec succès.' : 'SMS envoyé avec succès.' });
        this.sendingSms = false;
        this.showSmsDialog = false;
      },
      error: (error) => {
        this.sendingSms = false;
        let errorMessage = "Échec de l'envoi du SMS.";
        if (error && error.error && error.error.message) {
          this.messageService.add({ severity: 'error', summary: 'Erreur', detail: error.error.message });
        } else {
          this.messageService.add({ severity: 'error', summary: 'Erreur', detail: errorMessage });
        }
      }
    });
  }

  isPro(regime: string | undefined): boolean {
    if (!regime) return false;
    const lower = regime.toLowerCase();
    return lower.includes('tns') || lower.includes('indépendant') || lower.includes('ssi');
  }

  formatOrigine(origine: string | undefined): string {
    if (!origine) return 'Lead Fournisseur';
    const lower = origine.toLowerCase();
    if (lower === 'web') return 'Site Web';
    if (lower === 'import') return 'Import CSV';
    return origine;
  }

  getOrigineColor(origine: string | undefined): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
    if (!origine) return 'success';
    const lower = origine.toLowerCase();
    if (lower === 'web') return 'info';
    if (lower === 'import') return 'warning';
    return 'success';
  }

  // --- BULK EMAIL ACTIONS ---

  /**
   * Envoyer l'email "Économisez sans risque" à une seule comparaison
   */
  sendSavingsEmail(comparison: ComparisonHistory): void {
    if (!comparison.id) return;
    this.sendingSavingsEmailId = comparison.id;
    this.projectHistoryService.bulkStepOneSavingsEmail([comparison.id.toString()]).subscribe({
      next: (res) => {
        this.sendingSavingsEmailId = null;
        this.messageService.add({
          severity: 'success',
          summary: 'Email envoyé',
          detail: 'L\'email "Économisez" a été envoyé.'
        });
      },
      error: (err) => {
        this.sendingSavingsEmailId = null;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible d\'envoyer l\'email.'
        });
      }
    });
  }

  /**
   * Renvoyer le devis à une seule comparaison
   */
  resendDevis(comparison: ComparisonHistory): void {
    if (!comparison.id) return;
    this.resendingDevisId = comparison.id;
    this.projectHistoryService.bulkResendDevisEmail([comparison.id.toString()]).subscribe({
      next: (res) => {
        this.resendingDevisId = null;
        this.messageService.add({
          severity: 'success',
          summary: 'Devis renvoyé',
          detail: 'Le devis a été renvoyé avec succès.'
        });
      },
      error: (err) => {
        this.resendingDevisId = null;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de renvoyer le devis.'
        });
      }
    });
  }

  /**
   * Envoyer l'email "Économisez sans risque" aux comparaisons sélectionnées
   */
  bulkSendSavingsEmail(): void {
    if (this.selectedComparisons.length === 0) return;

    this.confirmationService.confirm({
      message: `Envoyer l'email "Économisez" à ${this.selectedComparisons.length} comparaison(s) sélectionnée(s) ?`,
      header: 'Confirmation',
      icon: 'pi pi-envelope',
      accept: () => {
        this.sendingSavingsEmail = true;
        this.projectHistoryService.bulkStepOneSavingsEmail(this.selectedComparisons).subscribe({
          next: (res) => {
            this.sendingSavingsEmail = false;
            this.messageService.add({
              severity: 'success',
              summary: 'Emails envoyés',
              detail: res.message || `${res.sent_project_ids?.length || 0} email(s) envoyé(s)`
            });
            if (res.failed_project_ids && res.failed_project_ids.length > 0) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Échecs',
                detail: `${res.failed_project_ids.length} email(s) en échec`
              });
            }
          },
          error: (err) => {
            this.sendingSavingsEmail = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: 'Erreur lors de l\'envoi des emails Économisez'
            });
            console.error(err);
          }
        });
      }
    });
  }

  /**
   * Renvoyer le devis original (retrouvé dans Gmail) aux comparaisons sélectionnées
   */
  bulkResendDevis(): void {
    if (this.selectedComparisons.length === 0) return;

    this.confirmationService.confirm({
      message: `Renvoyer le devis à ${this.selectedComparisons.length} comparaison(s) sélectionnée(s) ? Le devis sera retrouvé dans les emails envoyés Gmail.`,
      header: 'Confirmation',
      icon: 'pi pi-replay',
      accept: () => {
        this.resendingDevis = true;
        this.projectHistoryService.bulkResendDevisEmail(this.selectedComparisons).subscribe({
          next: (res) => {
            this.resendingDevis = false;
            this.messageService.add({
              severity: 'success',
              summary: 'Devis renvoyés',
              detail: res.message || `${res.sent_project_ids?.length || 0} devis renvoyé(s)`
            });
            if (res.failed_project_ids && res.failed_project_ids.length > 0) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Échecs',
                detail: `${res.failed_project_ids.length} renvoi(s) en échec`
              });
            }
          },
          error: (err) => {
            this.resendingDevis = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: 'Erreur lors du renvoi des devis'
            });
            console.error(err);
          }
        });
      }
    });
  }

  /**
   * Obtenir la couleur du tag pour le nombre de résultats
   */
  getResultsSeverity(totalResults: number): string {
    if (totalResults >= 10) return 'success';
    if (totalResults >= 5) return 'warning';
    return 'danger';
  }

  /**
   * Importation de fichier CSV
   */
  onCsvFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];

    if (!file) {
      return;
    }

    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.csv') && !lower.endsWith('.txt') && !lower.endsWith('.xlsx') && !lower.endsWith('.xls')) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Format invalide',
        detail: 'Veuillez sélectionner un fichier CSV ou Excel (.xlsx, .xls).'
      });
      input.value = '';
      return;
    }

    this.importingCsv = true;
    this.comparisonHistoryService.importCsv(file).subscribe({
      next: (result) => {
        const errorPreview = result.errors?.length
          ? ` ${Math.min(result.errors.length, 3)} erreur(s) exemple: ${result.errors.slice(0, 3).join(' | ')}`
          : '';
        const doublonInfo = result.doublons > 0 ? `, ${result.doublons} doublon(s)` : '';

        this.messageService.add({
          severity: result.skipped > 0 ? 'warn' : 'success',
          summary: 'Import terminé',
          detail: `${result.imported} projet(s) importé(s), ${result.skipped} ignoré(s)${doublonInfo}.${errorPreview}`,
          life: 12000
        });

        this.refreshFromDatabase();
        this.importingCsv = false;
        input.value = '';
      },
      error: (error) => {
        console.error('Erreur import CSV:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Import échoué',
          detail: error?.error?.detail || 'Impossible d\'importer le fichier CSV.'
        });
        this.importingCsv = false;
        input.value = '';
      }
    });
  }
}
